import { useState } from "react";
import { motion } from "framer-motion";
import { useLenis } from "lenis/react";
import { Check } from "lucide-react";
import { Reveal, SectionHeading, EASE } from "./Reveal";
import { OFFER, offerActive, discounted, eur } from "@/lib/offer";

const PLANS = [
  {
    id: "essential",
    name: "Nex Essential",
    target: "Piccoli B&B e botteghe",
    price: 990,
    features: [
      "Sito web su misura, 5 pagine",
      "Ottimizzazione mobile perfetta",
      "Scheda Google Business Profile",
      "Form contatti & mappa",
    ],
  },
  {
    id: "pro",
    name: "Nex Pro Direct",
    target: "Ristoranti e boutique hotel",
    price: 1990,
    popular: true,
    features: [
      "Sito web premium, 10 pagine",
      "Prenotazioni senza commissioni",
      "Menu interattivo / gestione camere",
      "SEO locale avanzata",
      "Ottimizzazione di foto e testi forniti dal cliente",
    ],
  },
  {
    id: "tailored",
    name: "Nex Tailored",
    target: "Resort, catene locali, e-commerce",
    price: 2990,
    features: [
      "Design 100% custom",
      "Integrazione booking engine / PMS",
      "Multilingua (IT, EN, DE, FR)",
      "Supporto e aggiornamenti per 12 mesi",
    ],
  },
];

export default function Pricing() {
  const [billing, setBilling] = useState("once");
  const lenis = useLenis();

  const go = (e) => {
    e.preventDefault();
    lenis?.scrollTo("#contatti", { offset: -84, duration: 1.5 });
  };

  return (
    <section id="piani" className="py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-8">
          <SectionHeading
            tag="Piani & listino"
            title={
              <>
                Prezzi chiari, senza <em className="italic text-terracotta">sorprese.</em>
              </>
            }
            sub={
              offerActive()
                ? `${OFFER.short} su tutti i piani per i primi clienti: valida fino al ${OFFER.deadlineLabel}.`
                : undefined
            }
          />
          <Reveal delay={0.2}>
            <div
              className="inline-flex rounded-full border border-line bg-panel p-1"
              role="group"
              aria-label="Modalità di pagamento"
            >
              <button
                type="button"
                onClick={() => setBilling("once")}
                data-testid="pricing-toggle-once"
                aria-pressed={billing === "once"}
                className={`rounded-full px-5 py-2.5 font-accent text-[11px] font-semibold uppercase tracking-[0.16em] transition-all duration-300 ${
                  billing === "once" ? "bg-terracotta text-[#1c0d08]" : "text-stone hover:text-bone"
                }`}
              >
                Una tantum
              </button>
              <button
                type="button"
                onClick={() => setBilling("monthly")}
                data-testid="pricing-toggle-monthly"
                aria-pressed={billing === "monthly"}
                className={`rounded-full px-5 py-2.5 font-accent text-[11px] font-semibold uppercase tracking-[0.16em] transition-all duration-300 ${
                  billing === "monthly" ? "bg-terracotta text-[#1c0d08]" : "text-stone hover:text-bone"
                }`}
              >
                12 rate mensili
              </button>
            </div>
          </Reveal>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-3">
          {PLANS.map((p, i) => (
            <motion.div
              key={p.id}
              data-testid={`pricing-card-${p.id}`}
              initial={{ opacity: 0, y: 36 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.85, delay: i * 0.08, ease: EASE }}
              className={`relative flex flex-col rounded-3xl border p-8 transition-colors duration-500 ${
                p.popular
                  ? "border-terracotta/60 bg-panel-2"
                  : "border-line bg-panel hover:border-line-strong"
              }`}
            >
              {p.popular && (
                <span className="absolute -top-3.5 left-8 rounded-full bg-terracotta px-4 py-1.5 font-accent text-[10px] font-semibold uppercase tracking-[0.2em] text-[#1c0d08]">
                  Consigliato
                </span>
              )}

              <h3 className="font-serif text-2xl tracking-tight text-bone">{p.name}</h3>
              <p className="mt-1.5 text-sm text-stone">{p.target}</p>

              <div className="mt-7 border-y border-line py-6">
                {offerActive() && (
                  <p className="mb-2 flex items-center gap-2 text-sm text-faded">
                    <span className="line-through" data-testid={`pricing-list-${p.id}`}>{eur(p.price)}</span>
                    <span className="rounded-full bg-ochre/15 px-2 py-0.5 font-accent text-[9px] font-semibold uppercase tracking-[0.18em] text-ochre">
                      −{OFFER.pct}% lancio
                    </span>
                  </p>
                )}
                {billing === "once" ? (
                  <>
                    <p className="font-serif text-5xl tracking-tight text-bone" data-testid={`pricing-price-${p.id}`}>
                      {eur(discounted(p.price))}
                    </p>
                    <p className="mt-2 text-xs text-faded">Pagamento unico, progetto chiavi in mano</p>
                  </>
                ) : (
                  <>
                    <p className="font-serif text-5xl tracking-tight text-bone" data-testid={`pricing-price-${p.id}`}>
                      {eur(Math.round(discounted(p.price) / 12))}
                      <span className="font-sans text-lg text-stone"> /mese</span>
                    </p>
                    <p className="mt-2 text-xs text-faded">
                      12 rate mensili · {eur(discounted(p.price))} complessivi, canone di cura incluso
                    </p>
                  </>
                )}
              </div>

              <ul className="mt-7 flex-1 space-y-3.5">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm text-stone">
                    <Check size={16} className="mt-0.5 shrink-0 text-terracotta" />
                    {f}
                  </li>
                ))}
              </ul>

              <a
                href="#contatti"
                onClick={go}
                data-testid={`pricing-cta-${p.id}`}
                className={`mt-8 inline-flex items-center justify-center rounded-full px-6 py-3.5 font-accent text-[11px] font-semibold uppercase tracking-[0.18em] transition-colors duration-300 ${
                  p.popular
                    ? "bg-terracotta text-[#1c0d08] hover:bg-[#f0695a]"
                    : "border border-line-strong text-bone hover:border-bone/50"
                }`}
              >
                Scegli {p.name.split(" ")[1]}
              </a>
            </motion.div>
          ))}
        </div>

        <Reveal delay={0.1}>
          <p className="mt-8 text-center text-sm text-faded">
            Tutti i piani includono hosting, dominio, formazione all'avvio e 30 giorni di supporto gratuito. Offerta lancio valida per i contratti firmati entro il 31 dicembre 2026. Foto e materiali non recuperabili online vengono forniti dal cliente.
          </p>
        </Reveal>
      </div>
    </section>
  );
}